import { _Notificare } from './plugin';
export class NotificareDeviceModule {
    /**
     * @returns {Promise<NotificareDevice | null>} - A promise that resolves to
     * the current {@link NotificareDevice} information, or 'null' in case no
     * device is registered.
     */
    async getCurrentDevice() {
        const { result } = await _Notificare.getCurrentDevice();
        return result;
    }
    /**
     * @returns {Promise<string | null>} - A promise that resolves to the
     * preferred language of the current device for notifications and messages, or
     * `null` if no preferred language is set.
     */
    async getPreferredLanguage() {
        const { result } = await _Notificare.getPreferredLanguage();
        return result;
    }
    /**
     * Updates the preferred language setting for the device.
     *
     * @param {string | null} language - The preferred language code.
     * @returns {Promise<void>} - A promise that resolves when the preferred language
     * has been successfully updated.
     */
    async updatePreferredLanguage(language) {
        await _Notificare.updatePreferredLanguage({ language });
    }
    /**
     * Registers a user for the device.
     *
     * To register the device anonymously, set both `userId` and `userName` to `null`.
     *
     * @param {string | null} userId - Optional user identifier.
     * @param {string | null} userName - Optional username.
     * @returns {Promise<void>} - A promise that resolves when the user has been
     * successfully registered.
     *
     * @deprecated Use updateUser() instead.
     */
    async register(userId, userName) {
        await _Notificare.register({ userId, userName });
    }
    /**
     * Updates the user information for the device.
     *
     * To register the device anonymously, set both `userId` and `userName` to `null`.
     *
     * @param {string | null} userId - Optional user identifier.
     * @param {string | null} userName - Optional username.
     * @returns {Promise<void>} - A promise that resolves when the user information
     * has been successfully updated.
     */
    async updateUser(userId, userName) {
        await _Notificare.updateUser({ userId, userName });
    }
    /**
     * Fetches the tags associated with the device.
     *
     * @return {Promise<string[]>} - A promise that resolves to a list of tags
     * currently associated with the device.
     */
    async fetchTags() {
        const { result } = await _Notificare.fetchTags();
        return result;
    }
    /**
     * Adds a single tag to the device.
     *
     * @param {string} tag - The tag to add.
     * @returns {Promise<void>} - A promise that resolves when the tag has been
     * successfully added to the device.
     */
    async addTag(tag) {
        await _Notificare.addTag({ tag });
    }
    /**
     * Adds multiple tags to the device.
     *
     * @param {string[]} tags - A list of tags to add.
     * @returns {Promise<void>} - A promise that resolves when all the tags have
     * been successfully added to the device.
     */
    async addTags(tags) {
        await _Notificare.addTags({ tags });
    }
    /**
     * Removes a specific tag from the device.
     *
     * @param {string} tag - The tag to remove.
     * @returns {Promise<void>} - A promise that resolves when the tag has been
     * successfully removed from the device.
     */
    async removeTag(tag) {
        await _Notificare.removeTag({ tag });
    }
    /**
     * Removes multiple tags from the device.
     *
     * @param {string[]} tags - A list of tags to remove.
     * @returns {Promise<void>} - A promise that resolves when all the specified tags
     * have been successfully removed from the device.
     */
    async removeTags(tags) {
        await _Notificare.removeTags({ tags });
    }
    /**
     * Clears all tags from the device.
     *
     * @returns {Promise<void>} - A promise that resolves when all tags have been
     * successfully cleared from the device.
     */
    async clearTags() {
        await _Notificare.clearTags();
    }
    /**
     * Fetches the "Do Not Disturb" (DND) settings for the device.
     *
     * @return {Promise<NotificareDoNotDisturb | null>} - A promise that resolves
     * to the current {@link NotificareDoNotDisturb} settings, or `null` if
     * none are set.
     */
    async fetchDoNotDisturb() {
        const { result } = await _Notificare.fetchDoNotDisturb();
        return result;
    }
    /**
     * Updates the "Do Not Disturb" (DND) settings for the device.
     *
     * @param {NotificareDoNotDisturb} dnd - The new {@link NotificareDoNotDisturb}
     * settings to apply.
     * @returns {Promise<void>} - A promise that resolves when the DND settings
     * have been successfully updated.
     */
    async updateDoNotDisturb(dnd) {
        await _Notificare.updateDoNotDisturb({ dnd });
    }
    /**
     * Clears the "Do Not Disturb" (DND) settings for the device.
     *
     * @returns {Promise<void>} - A promise that resolves when the DND settings
     * have been successfully cleared.
     */
    async clearDoNotDisturb() {
        await _Notificare.clearDoNotDisturb();
    }
    /**
     * Fetches the user data associated with the device.
     *
     * @return {Promise<Record<string, string>>} - A promise that resolves to a
     * {@link Record} object containing the current user data.
     */
    async fetchUserData() {
        const { result } = await _Notificare.fetchUserData();
        return result;
    }
    /**
     * Updates the custom user data associated with the device.
     *
     * @param {Record<string, string | null>} userData - The updated user data to associate
     * with the device.
     * @returns {Promise<void>} - A promise that resolves when the user data has
     * been successfully updated.
     */
    async updateUserData(userData) {
        await _Notificare.updateUserData({ userData });
    }
}
//# sourceMappingURL=notificare-device-module.js.map